/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/



export const EDITABLE_TYPES = Object.freeze([
  'text',
  'heading',
  'paragraph',
  'image',
  'button',
  'link',
  'card',
  'tile',
  'image grid',
]);

function normalizeType(raw) {
  return String(raw == null ? '' : raw)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function isEditableType(token) {
  return EDITABLE_TYPES.indexOf(normalizeType(token)) !== -1;
}


function readClassName(node) {
  if (!node) return '';
  if (typeof node.className === 'string') return node.className.toLowerCase();
  if (typeof node.getAttribute === 'function') {
    return String(node.getAttribute('class') || '').toLowerCase();
  }
  return '';
}

export function classifyElement(node) {
  if (!node || node.nodeType !== 1) return null;

  const explicit =
    typeof node.getAttribute === 'function'
      ? node.getAttribute('data-element-type')
      : null;
  if (explicit) {
    const normalized = normalizeType(explicit);
    return isEditableType(normalized) ? normalized : null;
  }

  const tag = String(node.tagName || '').toLowerCase();
  const className = readClassName(node);

  if (/^h[1-6]$/.test(tag)) return 'heading';
  if (tag === 'p') return 'paragraph';
  if (tag === 'img') return 'image';
  if (tag === 'a') return 'link';
  if (tag === 'button') return 'button';
  if (tag === 'input') {
    const inputType =
      typeof node.getAttribute === 'function'
        ? String(node.getAttribute('type') || '').toLowerCase()
        : '';
    if (inputType === 'button' || inputType === 'submit' || inputType === 'reset') {
      return 'button';
    }
  }

  if (/image[-_ ]?grid|gallery/.test(className)) return 'image grid';
  if (/\bcard\b|card/.test(className) || tag === 'article') return 'card';
  if (/tile/.test(className)) return 'tile';
  if (/\btext\b|text/.test(className)) return 'text';

  if (
    tag === 'span' ||
    tag === 'strong' ||
    tag === 'em' ||
    tag === 'small' ||
    tag === 'label' ||
    tag === 'figcaption' ||
    tag === 'blockquote'
  ) {
    return 'text';
  }

  return null;
}

export function isEditableElement(node) {
  if (typeof node === 'string') return isEditableType(node);
  const type = classifyElement(node);
  return type !== null && isEditableType(type);
}


export function computeElementId(sectionEl, elementEl) {
  if (!sectionEl || !elementEl || elementEl.nodeType !== 1) return '';

  const parts = [];
  let node = elementEl;
  while (node && node !== sectionEl && node.nodeType === 1) {
    const current = node;
    const tag = String(current.tagName || '').toLowerCase();
    const currentTagName = current.tagName;
    const parent = current.parentElement;
    const siblings = parent
      ? Array.prototype.filter.call(
          parent.children,
          (child) => child.tagName === currentTagName
        )
      : [current];
    const nth = Math.max(1, siblings.indexOf(current) + 1);
    parts.unshift(`${tag}:nth-of-type(${nth})`);
    node = parent;
  }

  const type = classifyElement(elementEl) || String(elementEl.tagName || '').toLowerCase();
  const path = parts.join('>');
  return path ? `${type}@${path}` : type;
}


export function resolveInnermostEditable(eventTarget) {
  let node =
    eventTarget && eventTarget.nodeType === 3
      ? eventTarget.parentElement
      : eventTarget;

  while (node && node.nodeType === 1) {
    if (isEditableElement(node)) return node;
    node = node.parentElement;
  }
  return null;
}
